import React, { useState, useEffect } from 'react';
import type { User, UpdateProfileData } from '@/types/user';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Edit3, 
  Calendar, 
  ChevronDown, 
  User as UserIcon,
  Loader2
} from 'lucide-react';
import { userService } from '@/services/userService';
import { useUser } from '@/contexts/UserContext';
import { toast } from '@/hooks/use-toast';

interface ProfileInformationProps {
  user: User;
}

interface Country {
  code: string;
  name: string;
}

const countries: Country[] = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CA', name: 'Canada' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'EE', name: 'Estonia' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MX', name: 'Mexico' },
  { code: 'MA', name: 'Morocco' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NO', name: 'Norway' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'KR', name: 'South Korea' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TR', name: 'Turkey' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' }
];

// Helper function to convert ISO date string to YYYY-MM-DD format for date input
const formatDateForInput = (dateString: string | undefined | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

export const ProfileInformation: React.FC<ProfileInformationProps> = ({ user }) => {
  const { updateUser } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dateInputRef = React.useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    dob: formatDateForInput(user.dob),
    country: user.country || '',
    gender: user.gender || '',
  });

  // Update form data when user changes
  useEffect(() => {
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      dob: formatDateForInput(user.dob),
      country: user.country || '',
      gender: user.gender || '',
    });
  }, [user]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const updateData: UpdateProfileData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: user.email, // Keep current email
        country: formData.country,
        gender: formData.gender as 'male' | 'female' | 'other' | undefined,
        dob: formData.dob,
      };

      const response = await userService.updateProfile(updateData);
      
      // Update tokens
      userService.updateTokens(response);
      
      // Update user context
      if (!response.user) {
        throw new Error('User data not found in response');
      }
      const updatedUser: User = {
        ...user,
        ...response.user,
        phone_code: response.user.phone_code || response.user.mobile_region_code,
        phone: response.user.phone || response.user.mobile_number,
        role: response.user.fk_role_id === 3 ? 'plus' : 'visitor',
        isAuthenticated: true,
      };
      updateUser(updatedUser);

      toast({
        title: "Success",
        description: "Profile updated successfully!",
      });

      setIsEditing(false);
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    // Reset form data to original user values
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      dob: formatDateForInput(user.dob),
      country: user.country || '',
      gender: user.gender || '',
    });
    setIsEditing(false);
  };

  return (
    <Card className="rounded-lg shadow-inner border-0 w-full max-w-full" style={{
      boxShadow: 'inset 0 0 10px rgba(96, 96, 96, 1)',
      backgroundColor: '#6A6A6A59'
    }}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white flex items-center gap-2 profile-typography">
          Profile Information
        </CardTitle>
        {!isEditing && (
          <Button
            onClick={handleEdit}
            variant="ghost"
            size="sm"
            className="bg-slate-900 text-gray-400 hover:text-white hover:bg-slate-600"
          >
            <Edit3 className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="profile-info-fields-group">
          {/* First Name */}
          <div className="profile-info-field-container">
            <label className="text-white font-medium profile-info-label">First Name</label>
            <div className="relative">
              <Input
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                disabled={!isEditing}
                className="profile-input profile-info-input text-white placeholder:text-gray-400"
                placeholder="Enter first name"
              />
            </div>
          </div>

          {/* Last Name */}
          <div className="profile-info-field-container">
            <label className="text-white font-medium profile-info-label">Last Name</label>
            <div className="relative">
              <Input
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                disabled={!isEditing}
                className="profile-input profile-info-input text-white placeholder:text-gray-400"
                placeholder="Enter last name"
              />
            </div>
          </div>

          {/* Date of Birth */}
          <div className="profile-info-field-container narrow">
            <label className="text-white font-medium profile-info-label">Date of Birth</label>
            <div className="relative">
              <Input
                ref={dateInputRef}
                type="date"
                value={formData.dob}
                onChange={(e) => handleInputChange('dob', e.target.value)}
                disabled={!isEditing}
                className="profile-input profile-info-input profile-date-input text-white placeholder:text-gray-400"
                placeholder="YYYY-MM-DD"
              />
              <div
                onClick={() => {
                  if (isEditing && dateInputRef.current && 'showPicker' in dateInputRef.current) {
                    (dateInputRef.current as HTMLInputElement).showPicker();
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer z-10 hover:opacity-80"
                style={{ pointerEvents: isEditing ? 'auto' : 'none' }}
              >
                <Calendar className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>

          {/* Country */}
          <div className="profile-info-field-container">
            <label className="text-white font-medium profile-info-label">Country</label>
            <div className="relative">
              <select
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                disabled={!isEditing}
                className="profile-select profile-info-input text-white placeholder:text-gray-400 w-full appearance-none border border-gray-600 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="" className="bg-gray-800 text-white profile-typography">Enter country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code} className="bg-gray-800 text-white profile-typography">
                    {country.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Gender */}
          <div className="profile-info-field-container narrow">
            <label className="text-white font-medium profile-info-label">Gender</label>
            <div className="relative">
              <select
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                disabled={!isEditing}
                className="profile-select profile-info-input text-white placeholder:text-gray-400 w-full appearance-none border border-gray-600 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="" className="bg-gray-800 text-white profile-typography">Select gender</option>
                <option value="male" className="bg-gray-800 text-white profile-typography">Male</option>
                <option value="female" className="bg-gray-800 text-white profile-typography">Female</option>
                <option value="other" className="bg-gray-800 text-white profile-typography">Other</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Empty spacer for right column when editing */}
          {isEditing && <div></div>}

          {/* Action Buttons - positioned under Gender in left column */}
          {isEditing && (
            <div className="profile-info-field-container narrow flex flex-row gap-3 pt-2 h-auto">
              <Button
                onClick={handleCancel}
                disabled={isLoading}
                variant="outline"
                className="flex-1 profile-button profile-button-cancel profile-typography"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isLoading}
                className="flex-1 profile-button profile-button-save text-white profile-typography"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
